import React, { useEffect, useState } from "react";
import { List, ListItemButton, ListItemText, Chip, Box } from "@mui/material";
import { stepsConfig } from "../step-config";
import { useStepCompletion } from "../hooks/useStepCompletion";
import { createAxiosInstance } from "../../../api";
import { useSelector } from "react-redux";

type Props = {
  activeIndex: number;
  onSelect: (i: number) => void;
};

export default function StepSidebar({ activeIndex, onSelect }: Props) {
  const completions = useStepCompletion();
  const userId = useSelector((state: any) => state.main?.userId);
  const [hasSubjects, setHasSubjects] = useState(false);

  useEffect(() => {
    if (!userId) return;
    createAxiosInstance()
      .get(`${process.env.REACT_APP_SERVER}/api/subjects/user/${userId}`)
      .then((res) => {
        const subjects = res.data || [];
        setHasSubjects(subjects.length > 0);
      })
      .catch(() => setHasSubjects(false));
  }, [userId]);

  // Update completions with actual subjects status
  const updatedCompletions = {
    ...completions,
    subjects: { ...completions.subjects, isCompleted: hasSubjects },
  };

  return (
    <Box className="onboarding-sidebar">
      <List sx={{ p: 0 }}>
        {stepsConfig.map((s, i) => {
          const status = updatedCompletions[s.id as keyof typeof updatedCompletions];
          const isCompleted = status?.isCompleted || false;
          const isOptional = status?.isOptional || false;
          
          let chipLabel = "";
          let chipColor: "success" | "primary" | "default" = "default";
          let chipVariant: "filled" | "outlined" = "outlined";
          
          // Show "Done" for any completed step, regardless of active index
          if (isCompleted && i !== activeIndex) {
            chipLabel = "Done";
            chipColor = "success";
            chipVariant = "filled";
          } else if (i === activeIndex) {
            chipLabel = "Now";
            chipColor = "primary";
            chipVariant = "filled";
          }
          
          return (
            <ListItemButton 
              key={s.id} 
              selected={i === activeIndex} 
              onClick={() => onSelect(i)} 
              sx={{ 
                borderRadius: 1, 
                mb: 0.5, 
                alignItems: 'flex-start', 
                '&.Mui-selected': { bgcolor: 'rgba(113,45,148,0.06)' } 
              }}
            >
              <ListItemText 
                primary={s.title} 
                secondary={s.description} 
                primaryTypographyProps={{ fontWeight: 600 }} 
              />
              {chipLabel && (
                <Chip 
                  size="small" 
                  sx={{ mt: 0.5 }} 
                  label={chipLabel} 
                  color={chipColor} 
                  variant={chipVariant} 
                />
              )}
            </ListItemButton>
          );
        })}
      </List>
    </Box>
  );
}
